import { webCoreTypes } from "@core/core-types.di.ts";
import {
    DoneLogoutEventBus,
    UnauthorizedEventBus,
} from "@core/domain/models/event-bus.model.ts";
import type { EventBusRepository } from "@core/domain/repositories/event-bus.repository.ts";
import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import {
    AuthenticatedUserLoadCurrentUseCase,
    AuthenticationLogoutUseCase,
} from "@iot-platform/authentication";
import { BaseViewModel } from "@iot-platform/core";
import type { SpatialLevelRepository } from "@station/domain/repositories/spatial-level.repository.ts";
import { stationsTypes } from "@station/station-types.di.ts";
import { t } from "i18next";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class AppViewModel extends BaseViewModel {
    @observable
    loading = true;

    constructor(
        @inject(webCoreTypes.repositories.EventBusRepository)
        private readonly eventBusRepository: EventBusRepository,
        @inject(AuthenticationLogoutUseCase)
        private readonly authenticationLogoutUseCase: AuthenticationLogoutUseCase,
        @inject(AuthenticatedUserLoadCurrentUseCase)
        private readonly authenticatedUserLoadCurrentUseCase: AuthenticatedUserLoadCurrentUseCase,
        @inject(stationsTypes.repositories.SpatialLevelRepository)
        private readonly spatialLevelRepository: SpatialLevelRepository,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        this.addSub(
            this.eventBusRepository.bus.subscribe((event) => {
                if (event instanceof UnauthorizedEventBus) {
                    void this.authenticationLogoutUseCase.execute("local");
                }
            }),
        );
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await this.loadCurrentUser();
    }

    @action
    private async loadCurrentUser(): Promise<void> {
        await this.authenticatedUserLoadCurrentUseCase.execute();

        runInAction(() => {
            this.loading = false;
        });
    }

    attachExit(exitCallback: () => void): void {
        this.addSub(
            this.eventBusRepository.bus.subscribe((event) => {
                if (event instanceof UnauthorizedEventBus) {
                    void this.authenticationLogoutUseCase.execute("local");
                    exitCallback();
                } else if (event instanceof DoneLogoutEventBus) {
                    this.spatialLevelRepository.emptySpatialLevels();
                    ToastManagerRepository.error(
                        t("error:authentication.logout.justLoggedOut"),
                    );
                }
            }),
        );
    }
}
